import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { LeadForm } from '../components/LeadForm';
import { useCreateLead, useLead, useUpdateLead } from '../queries';
import type { LeadInput } from '../types';

export function LeadFormPage() {
  const { publicId = '' } = useParams();
  const isEdit = Boolean(publicId);
  const leadId = publicId;
  const navigate = useNavigate();

  const leadQuery = useLead(isEdit ? leadId : '');
  const create = useCreateLead();
  const update = useUpdateLead(leadId);

  async function handleSubmit(values: LeadInput) {
    const saved = isEdit ? await update.mutateAsync(values) : await create.mutateAsync(values);
    navigate(`/crm/leads/${saved.public_id}`);
  }

  if (isEdit && leadQuery.isPending) {
    return (
      <div className="max-w-xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  const lead = leadQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(isEdit ? `/crm/leads/${leadId}` : '/crm/leads')}
        >
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? `Edit ${lead?.title ?? 'lead'}` : 'New lead'}
        </h1>
      </div>

      <LeadForm
        submitLabel={isEdit ? 'Save changes' : 'Create lead'}
        defaultValues={
          lead
            ? {
                title: lead.title,
                description: lead.description,
                source: lead.source,
                status: lead.status,
                company: lead.company ? String(lead.company) : '',
                contact: lead.contact ? String(lead.contact) : '',
                owner: lead.owner ? String(lead.owner) : '',
                pain: lead.pain,
                icp_fit: lead.icp_fit,
                icp_segment: lead.icp_segment ? String(lead.icp_segment) : '',
              }
            : undefined
        }
        currentSegmentName={lead?.icp_segment_name}
        onSubmit={handleSubmit}
        onCancel={() => navigate(isEdit ? `/crm/leads/${leadId}` : '/crm/leads')}
      />
    </div>
  );
}
